import classNames from 'classnames'

import {
  EMPTY_FUNCTION,
  EMPTY_LIST,
  EMPTY_OBJECT,
} from '../../../constants/common'
import { getQaSelector } from '../../../utils/common'
import {
  GRETA_SKELETON_DELAY_MS,
  GretaAIChatStatus,
} from '../GretaAIChat.constants'
import { useGretaAIChatDelayedFlag } from '../GretaAIChat.hooks'
import {
  getIsGretaAIChatLoading,
  getIsGretaAIChatThinking,
} from '../GretaAIChat.utils'
import { GretaAIChatAnswerError } from '../GretaAIChatAnswerError'
import { getGretaAIChatRenderableTurns } from '../GretaAIChatMessage'
import { GretaAIChatScrollToBottomButton } from '../GretaAIChatScrollToBottomButton'
import { GretaAIChatThinkingIndicator } from '../GretaAIChatThinkingIndicator'

import { QA_NAME_GRETA_AI_CHAT_MESSAGE_LIST } from './GretaAIChatMessageList.constants'
import { useGretaAIChatAutoScroll } from './GretaAIChatMessageList.hooks'
import {
  getGretaAIChatFeedContent,
  getGretaAIChatFeedContentMode,
} from './GretaAIChatMessageList.utils'

import * as styles from './GretaAIChatMessageList.module.css'

function GretaAIChatMessageList({
  displayName,
  feedbackByMessageId = EMPTY_OBJECT,
  isLimitReached = false,
  isNewChat = false,
  messages = EMPTY_LIST,
  pendingAnswerFileName = null,
  status = GretaAIChatStatus.Idle,
  suggestedPrompts = EMPTY_LIST,
  turnStartedAt = null,
  onLoadOlderMessages = EMPTY_FUNCTION,
  onReloadMessages = EMPTY_FUNCTION,
  onRetry = EMPTY_FUNCTION,
  onSelectPrompt = EMPTY_FUNCTION,
  onSendPrompt = EMPTY_FUNCTION,
  onSubmitFeedback = EMPTY_FUNCTION,
}) {
  const turns = getGretaAIChatRenderableTurns(messages)

  const isHistoryLoading = getIsGretaAIChatLoading(status) && turns.length === 0

  const isSkeletonShown = useGretaAIChatDelayedFlag(
    isHistoryLoading,
    GRETA_SKELETON_DELAY_MS
  )

  const contentMode = getGretaAIChatFeedContentMode({
    isNewChat,
    isSkeletonShown,
    status,
    turnsCount: turns.length,
  })

  const {
    anchorRef,
    handleScroll,
    handleScrollToBottom,
    scrollRef,
    showScrollToBottom,
    spacerRef,
  } = useGretaAIChatAutoScroll(turns, { onLoadOlderMessages })

  const qaProps = getQaSelector({ value: QA_NAME_GRETA_AI_CHAT_MESSAGE_LIST })

  const wrapperClassName = classNames(styles.wrapper, {
    [styles.noFade]: isLimitReached,
  })

  const feedContent = getGretaAIChatFeedContent({
    anchorRef,
    contentMode,
    displayName,
    feedbackByMessageId,
    isLimitReached,
    suggestedPrompts,
    turns,
    onRetry,
    onSelectPrompt,
    onSendPrompt,
    onSubmitFeedback,
  })

  return (
    <div className={wrapperClassName}>
      <div
        {...qaProps}
        className={styles.list}
        ref={scrollRef}
        onScroll={handleScroll}
      >
        <div className={styles.column}>
          {feedContent}

          {getIsGretaAIChatThinking(status) ? (
            <GretaAIChatThinkingIndicator
              className={styles.turnGap}
              fileName={pendingAnswerFileName}
              turnStartedAt={turnStartedAt}
            />
          ) : null}

          <GretaAIChatAnswerError
            isFeedEmpty={turns.length === 0}
            isLimitReached={isLimitReached}
            status={status}
            onReloadMessages={onReloadMessages}
          />

          <div className={styles.turnSpacer} ref={spacerRef} />
        </div>
      </div>

      <GretaAIChatScrollToBottomButton
        isShown={showScrollToBottom && turns.length > 0}
        onClick={handleScrollToBottom}
      />
    </div>
  )
}

export default GretaAIChatMessageList
